/**
 * SuperadminBilling · v3.7.0 · Feb 2026
 * ---------------------------------------
 * UI per gestire il TotalKlick Billing Core (Billing-as-a-Service).
 * Consente al superadmin di:
 *   - Vedere tutti i prodotti registrati e i loro piani
 *   - Creare un nuovo prodotto (namespace)
 *   - Aggiungere/modificare/eliminare piani per un prodotto
 *   - Impostare Stripe/PayPal price_id per attivare i checkout
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, CreditCard, Package, Plus, Trash2, Edit3,
    ExternalLink, X, Loader2, Save, Zap,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const BASE_STYLE = {
    background: "#05050F",
    color: "#fff",
    fontFamily: "Geist, Outfit, sans-serif",
};

export default function SuperadminBilling() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [showNewPlan, setShowNewPlan] = useState(null); // product_id
    const [editingPlan, setEditingPlan] = useState(null); // plan object

    useEffect(() => {
        document.title = "Billing · Mission Control";
        loadCatalog();
    }, []);

    const loadCatalog = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/billing/registry/catalog");
            const productsWithPricing = await Promise.all(
                data.products.map(async (p) => {
                    // Il catalogo pubblico nasconde price_id — per la UI admin
                    // richiediamo i piani completi via l'endpoint dedicato.
                    try {
                        const { data: plansData } = await api.get(
                            `/billing/registry/products/${p.product_id}/plans`,
                        );
                        return { ...p, plans: plansData.plans || [] };
                    } catch (_) {
                        return p;
                    }
                }),
            );
            setProducts(productsWithPricing);
        } catch (e) {
            setError(formatApiError(e));
        }
        setLoading(false);
    };

    const handleDeleteProduct = async (product_id) => {
        if (!window.confirm(`Disattivare il prodotto "${product_id}"? I piani associati saranno anche disattivati.`)) return;
        try {
            await api.delete(`/billing/registry/products/${product_id}`);
            await loadCatalog();
        } catch (e) { alert(formatApiError(e)); }
    };

    const handleDeletePlan = async (plan_id) => {
        if (!window.confirm(`Disattivare il piano "${plan_id}"?`)) return;
        try {
            await api.delete(`/billing/registry/plans/${plan_id}`);
            await loadCatalog();
        } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <div className="min-h-screen px-6 py-8" style={BASE_STYLE} data-testid="superadmin-billing">
            <div className="max-w-6xl mx-auto">
                <Link
                    to="/superadmin"
                    className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6"
                    data-testid="billing-back-mc"
                >
                    <ArrowLeft size={12} /> Mission Control
                </Link>

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#4F8CFF,#6E5CFF)" }}>
                            <CreditCard size={18} color="#fff" />
                        </div>
                        <div>
                            <h1 className="text-white text-[24px]" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>
                                Billing Core
                            </h1>
                            <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {products.length} prodotti registrati · TotalKlick Billing-as-a-Service
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowNewProduct(true)}
                        data-testid="billing-new-product-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium hover:opacity-90 transition-opacity"
                        style={{ background: "linear-gradient(135deg,#4F8CFF,#6E5CFF)", color: "#fff" }}
                    >
                        <Plus size={14} /> Nuovo Prodotto
                    </button>
                </div>

                {loading && (
                    <div className="text-center py-16 text-white/55" data-testid="billing-loading">
                        <Loader2 className="inline animate-spin mr-2" size={16} /> Caricamento catalogo…
                    </div>
                )}

                {error && (
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-[13px] text-red-200 mb-6" data-testid="billing-error">
                        {error}
                    </div>
                )}

                {!loading && products.length === 0 && !error && (
                    <div className="text-center py-16 border border-white/10 rounded-xl" data-testid="billing-empty">
                        <Package size={32} className="mx-auto text-white/30 mb-3" />
                        <div className="text-white/70 text-[14px]">Nessun prodotto registrato.</div>
                        <div className="text-white/40 text-[12px] mt-1">Clicca &quot;Nuovo Prodotto&quot; per iniziare.</div>
                    </div>
                )}

                {/* Product cards */}
                {!loading && products.map((product) => (
                    <ProductCard
                        key={product.product_id}
                        product={product}
                        onAddPlan={() => setShowNewPlan(product.product_id)}
                        onEditPlan={(plan) => setEditingPlan(plan)}
                        onDeletePlan={handleDeletePlan}
                        onDeleteProduct={() => handleDeleteProduct(product.product_id)}
                    />
                ))}

                {/* Modals */}
                {showNewProduct && (
                    <NewProductModal
                        onClose={() => setShowNewProduct(false)}
                        onSaved={async () => { setShowNewProduct(false); await loadCatalog(); }}
                    />
                )}
                {showNewPlan && (
                    <NewPlanModal
                        productId={showNewPlan}
                        onClose={() => setShowNewPlan(null)}
                        onSaved={async () => { setShowNewPlan(null); await loadCatalog(); }}
                    />
                )}
                {editingPlan && (
                    <EditPlanModal
                        plan={editingPlan}
                        onClose={() => setEditingPlan(null)}
                        onSaved={async () => { setEditingPlan(null); await loadCatalog(); }}
                    />
                )}
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════
// ProductCard
// ══════════════════════════════════════════════════════════
function ProductCard({ product, onAddPlan, onEditPlan, onDeletePlan, onDeleteProduct }) {
    return (
        <div
            data-testid={`billing-product-${product.product_id}`}
            className="border border-white/10 rounded-xl p-5 mb-4 hover:border-white/20 transition-colors"
            style={{ background: "rgba(255,255,255,0.02)" }}
        >
            <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h2 className="text-white text-[18px]" style={{ fontWeight: 500 }}>{product.name}</h2>
                        <code className="text-[11px] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            {product.product_id}
                        </code>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${product.active ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                            {product.active ? "attivo" : "disattivato"}
                        </span>
                    </div>
                    {product.description && (
                        <p className="text-white/55 text-[13px] mt-1">{product.description}</p>
                    )}
                    {product.owner_tenant && (
                        <div className="text-white/40 text-[11px] mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            owner: {product.owner_tenant}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onAddPlan}
                        data-testid={`billing-add-plan-${product.product_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] border border-white/15 hover:border-white/30 hover:bg-white/5"
                    >
                        <Plus size={12} /> Piano
                    </button>
                    <button
                        onClick={onDeleteProduct}
                        data-testid={`billing-delete-product-${product.product_id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-red-300"
                        title="Disattiva prodotto"
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            {/* Plans table */}
            {product.plans && product.plans.length > 0 ? (
                <div className="grid gap-2">
                    {product.plans.map((plan) => (
                        <PlanRow
                            key={plan.plan_id}
                            plan={plan}
                            onEdit={() => onEditPlan(plan)}
                            onDelete={() => onDeletePlan(plan.plan_id)}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-white/40 text-[12px] italic py-3 pl-2">Nessun piano configurato.</div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════
// PlanRow
// ══════════════════════════════════════════════════════════
function PlanRow({ plan, onEdit, onDelete }) {
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const hasStripeLive = plan.stripe_price_id_monthly || plan.stripe_price_id_yearly;
    const hasPaypalLive = plan.paypal_plan_id_monthly || plan.paypal_plan_id_yearly;
    const isFree = plan.monthly_eur === 0 && plan.yearly_eur === 0;
    const canSyncStripe = !isFree && (!plan.stripe_price_id_monthly || !plan.stripe_price_id_yearly);

    const doSyncStripe = async () => {
        if (!window.confirm(
            `Creare automaticamente Product + Prices su Stripe per "${plan.plan_id}"?\n\n` +
            `Verranno creati:\n` +
            (!plan.stripe_price_id_monthly && plan.monthly_eur > 0 ? `  • Price monthly €${plan.monthly_eur}\n` : "") +
            (!plan.stripe_price_id_yearly && plan.yearly_eur > 0 ? `  • Price yearly €${plan.yearly_eur}\n` : "") +
            `\nUsa la chiave STRIPE_API_KEY corrente (test o live). Continuare?`
        )) return;
        setSyncing(true);
        try {
            const { data } = await api.post(`/billing/registry/plans/${plan.plan_id}/sync-stripe`);
            setSyncResult({ ok: true, events: data.events });
            window.setTimeout(() => window.location.reload(), 1500);
        } catch (e) {
            setSyncResult({ ok: false, err: formatApiError(e) });
        }
        setSyncing(false);
    };

    return (
        <div
            data-testid={`billing-plan-${plan.plan_id}`}
            className="flex items-center justify-between px-4 py-3 rounded-lg border border-white/8 hover:border-white/15"
            style={{ background: "rgba(255,255,255,0.02)" }}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                    <span className="text-white text-[14px]" style={{ fontWeight: 500 }}>{plan.label}</span>
                    <code className="text-[10px] text-white/35" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {plan.plan_id}
                    </code>
                    {plan.trial_days > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200">
                            trial {plan.trial_days}gg
                        </span>
                    )}
                    {!plan.active && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">disattivato</span>
                    )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[12px]">
                    <span className="text-white/70">
                        {isFree ? (
                            <span className="text-green-300">Free</span>
                        ) : (
                            <>€{plan.monthly_eur}/m · €{plan.yearly_eur}/y</>
                        )}
                    </span>
                    {!isFree && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${hasStripeLive ? "bg-green-500/15 text-green-300" : "bg-white/5 text-white/40"}`}>
                            Stripe: {hasStripeLive ? "ok" : "no price_id"}
                        </span>
                    )}
                    {!isFree && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${hasPaypalLive ? "bg-green-500/15 text-green-300" : "bg-white/5 text-white/40"}`}>
                            PayPal: {hasPaypalLive ? "ok" : "no plan_id"}
                        </span>
                    )}
                </div>
                {plan.entitlements && plan.entitlements.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {plan.entitlements.slice(0, 6).map((ent) => (
                            <code
                                key={ent}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-200"
                                style={{ fontFamily: "JetBrains Mono, monospace" }}
                            >
                                {ent}
                            </code>
                        ))}
                        {plan.entitlements.length > 6 && (
                            <span className="text-[10px] text-white/40">+{plan.entitlements.length - 6}</span>
                        )}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {canSyncStripe && (
                    <button
                        onClick={doSyncStripe}
                        disabled={syncing}
                        data-testid={`billing-sync-stripe-${plan.plan_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-purple-400/40 hover:bg-purple-500/10 text-purple-200 text-[11px] disabled:opacity-50"
                        title="Crea Product + Prices su Stripe"
                    >
                        {syncing ? <Loader2 className="animate-spin" size={11} /> : <Zap size={11} />}
                        Sync Stripe
                    </button>
                )}
                <button
                    onClick={onEdit}
                    data-testid={`billing-edit-plan-${plan.plan_id}`}
                    className="w-8 h-8 rounded-lg border border-white/15 hover:bg-white/5 grid place-items-center"
                    title="Modifica"
                >
                    <Edit3 size={12} />
                </button>
                <button
                    onClick={onDelete}
                    data-testid={`billing-delete-plan-${plan.plan_id}`}
                    className="w-8 h-8 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-red-300 grid place-items-center"
                    title="Disattiva"
                >
                    <Trash2 size={12} />
                </button>
            </div>
            {syncResult && (
                <div className={`absolute mt-2 text-[11px] ${syncResult.ok ? "text-green-300" : "text-red-300"}`}>
                    {syncResult.ok ? `✅ Sync ok: ${(syncResult.events||[]).join(", ")}` : `❌ ${syncResult.err}`}
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════
// Modali
// ══════════════════════════════════════════════════════════
function ModalShell({ title, onClose, children, testid }) {
    return (
        <div
            className="fixed inset-0 z-50 grid place-items-center p-4"
            style={{ background: "rgba(0,0,0,0.7)" }}
            data-testid={testid}
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg rounded-xl border border-white/10 p-6"
                style={{ background: "#0A0A1A" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-white text-[16px]" style={{ fontWeight: 500 }}>{title}</h3>
                    <button onClick={onClose} className="text-white/50 hover:text-white" data-testid={`${testid}-close`}>
                        <X size={16} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block mb-3">
            <div className="text-white/70 text-[11px] uppercase tracking-wider mb-1">{label}</div>
            {children}
            {hint && <div className="text-white/40 text-[11px] mt-1">{hint}</div>}
        </label>
    );
}

const inputCls =
    "w-full px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-white text-[13px] " +
    "placeholder:text-white/30 focus:border-white/40 focus:outline-none";

function NewProductModal({ onClose, onSaved }) {
    const [f, setF] = useState({ product_id: "", name: "", description: "", category: "saas", owner_tenant: "" });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            await api.post("/billing/registry/products", {
                product_id: f.product_id.trim().toLowerCase(),
                name: f.name.trim(),
                description: f.description.trim(),
                category: f.category.trim() || "saas",
                owner_tenant: f.owner_tenant.trim() || null,
            });
            onSaved();
        } catch (e) { setErr(formatApiError(e)); }
        setSaving(false);
    };
    return (
        <ModalShell title="Nuovo Prodotto" onClose={onClose} testid="billing-new-product-modal">
            <form onSubmit={submit}>
                <Field label="Product ID" hint="Namespace univoco · lowercase, no spazi (es. totalpl, ariabar)">
                    <input
                        data-testid="new-product-id"
                        className={inputCls}
                        placeholder="totalpl"
                        value={f.product_id}
                        onChange={(e) => setF({ ...f, product_id: e.target.value })}
                        required
                        pattern="^[a-z][a-z0-9_.\-]*$"
                    />
                </Field>
                <Field label="Nome visualizzato">
                    <input data-testid="new-product-name" className={inputCls} placeholder="TotalPL" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
                </Field>
                <Field label="Descrizione">
                    <textarea data-testid="new-product-description" className={inputCls} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                </Field>
                <Field label="Owner tenant" hint="Facoltativo · usato quando l'agent proprietario è terzo">
                    <input data-testid="new-product-owner" className={inputCls} placeholder="agent_X" value={f.owner_tenant} onChange={(e) => setF({ ...f, owner_tenant: e.target.value })} />
                </Field>
                {err && <div className="text-red-300 text-[12px] mb-3">{err}</div>}
                <button
                    type="submit"
                    disabled={saving}
                    data-testid="new-product-submit"
                    className="w-full py-2.5 rounded-lg font-medium text-[13px] hover:opacity-90 disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#4F8CFF,#6E5CFF)", color: "#fff" }}
                >
                    {saving ? <Loader2 className="inline animate-spin" size={14} /> : <><Save size={14} className="inline mr-1" /> Crea prodotto</>}
                </button>
            </form>
        </ModalShell>
    );
}

function NewPlanModal({ productId, onClose, onSaved }) {
    return <PlanFormModal
        title={`Nuovo piano · ${productId}`}
        testid="billing-new-plan-modal"
        initial={{ plan_slug: "", label: "", description: "", monthly_eur: 0, yearly_eur: 0, trial_days: 0, entitlements: "", stripe_price_id_monthly: "", stripe_price_id_yearly: "", paypal_plan_id_monthly: "", paypal_plan_id_yearly: "" }}
        onClose={onClose}
        onSubmit={async (payload) => {
            await api.post(`/billing/registry/products/${productId}/plans`, payload);
            onSaved();
        }}
    />;
}

function EditPlanModal({ plan, onClose, onSaved }) {
    return <PlanFormModal
        title={`Modifica · ${plan.plan_id}`}
        testid="billing-edit-plan-modal"
        readonly={["plan_slug"]}
        initial={{
            plan_slug: plan.plan_slug,
            label: plan.label,
            description: plan.description || "",
            monthly_eur: plan.monthly_eur, yearly_eur: plan.yearly_eur,
            trial_days: plan.trial_days,
            entitlements: (plan.entitlements || []).join(", "),
            stripe_price_id_monthly: plan.stripe_price_id_monthly || "",
            stripe_price_id_yearly: plan.stripe_price_id_yearly || "",
            paypal_plan_id_monthly: plan.paypal_plan_id_monthly || "",
            paypal_plan_id_yearly: plan.paypal_plan_id_yearly || "",
        }}
        onClose={onClose}
        onSubmit={async (payload) => {
            // PATCH non accetta plan_slug
            const { plan_slug: _slug, ...patch } = payload;
            await api.patch(`/billing/registry/plans/${plan.plan_id}`, patch);
            onSaved();
        }}
    />;
}

function PlanFormModal({ title, testid, initial, onClose, onSubmit, readonly = [] }) {
    const [f, setF] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const payload = {
                plan_slug: f.plan_slug.trim().toLowerCase(),
                label: f.label.trim(),
                description: f.description.trim(),
                monthly_eur: Number(f.monthly_eur) || 0,
                yearly_eur: Number(f.yearly_eur) || 0,
                trial_days: Number(f.trial_days) || 0,
                entitlements: f.entitlements.split(",").map((s) => s.trim()).filter(Boolean),
                stripe_price_id_monthly: f.stripe_price_id_monthly.trim() || null,
                stripe_price_id_yearly: f.stripe_price_id_yearly.trim() || null,
                paypal_plan_id_monthly: f.paypal_plan_id_monthly.trim() || null,
                paypal_plan_id_yearly: f.paypal_plan_id_yearly.trim() || null,
            };
            await onSubmit(payload);
        } catch (e) { setErr(formatApiError(e)); }
        setSaving(false);
    };
    const isRO = (k) => readonly.includes(k);
    return (
        <ModalShell title={title} onClose={onClose} testid={testid}>
            <form onSubmit={submit}>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Slug" hint="lowercase, no spazi">
                        <input data-testid="plan-slug" className={inputCls} value={f.plan_slug} onChange={(e) => setF({ ...f, plan_slug: e.target.value })} required disabled={isRO("plan_slug")} pattern="^[a-z0-9_.\-]+$" />
                    </Field>
                    <Field label="Label visualizzato">
                        <input data-testid="plan-label" className={inputCls} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} required />
                    </Field>
                    <Field label="€/mese">
                        <input data-testid="plan-monthly" type="number" step="0.01" className={inputCls} value={f.monthly_eur} onChange={(e) => setF({ ...f, monthly_eur: e.target.value })} />
                    </Field>
                    <Field label="€/anno">
                        <input data-testid="plan-yearly" type="number" step="0.01" className={inputCls} value={f.yearly_eur} onChange={(e) => setF({ ...f, yearly_eur: e.target.value })} />
                    </Field>
                    <Field label="Trial (giorni)">
                        <input data-testid="plan-trial" type="number" className={inputCls} value={f.trial_days} onChange={(e) => setF({ ...f, trial_days: e.target.value })} />
                    </Field>
                    <Field label="Descrizione">
                        <input data-testid="plan-description" className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                    </Field>
                </div>
                <Field label="Entitlements" hint="Separati da virgola · es: pl.commesse.unlim, pl.storage.50gb">
                    <textarea data-testid="plan-entitlements" className={inputCls} rows={2} value={f.entitlements} onChange={(e) => setF({ ...f, entitlements: e.target.value })} />
                </Field>
                <div className="mt-4 pt-3 border-t border-white/10">
                    <div className="text-white/50 text-[11px] uppercase tracking-wider mb-2">Provider IDs (per checkout LIVE)</div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Stripe price monthly">
                            <input data-testid="plan-stripe-monthly" className={inputCls} placeholder="price_..." value={f.stripe_price_id_monthly} onChange={(e) => setF({ ...f, stripe_price_id_monthly: e.target.value })} />
                        </Field>
                        <Field label="Stripe price yearly">
                            <input data-testid="plan-stripe-yearly" className={inputCls} placeholder="price_..." value={f.stripe_price_id_yearly} onChange={(e) => setF({ ...f, stripe_price_id_yearly: e.target.value })} />
                        </Field>
                        <Field label="PayPal plan monthly">
                            <input data-testid="plan-paypal-monthly" className={inputCls} placeholder="P-..." value={f.paypal_plan_id_monthly} onChange={(e) => setF({ ...f, paypal_plan_id_monthly: e.target.value })} />
                        </Field>
                        <Field label="PayPal plan yearly">
                            <input data-testid="plan-paypal-yearly" className={inputCls} placeholder="P-..." value={f.paypal_plan_id_yearly} onChange={(e) => setF({ ...f, paypal_plan_id_yearly: e.target.value })} />
                        </Field>
                    </div>
                </div>
                {err && <div className="text-red-300 text-[12px] mb-3 mt-2">{err}</div>}
                <button
                    type="submit"
                    disabled={saving}
                    data-testid="plan-submit"
                    className="w-full mt-3 py-2.5 rounded-lg font-medium text-[13px] hover:opacity-90 disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#4F8CFF,#6E5CFF)", color: "#fff" }}
                >
                    {saving ? <Loader2 className="inline animate-spin" size={14} /> : <><Save size={14} className="inline mr-1" /> Salva piano</>}
                </button>
            </form>
        </ModalShell>
    );
}
